/**
 * @voltxampere/spice — Diode codec (D)
 *
 * Berkeley SPICE3 syntax: `D{name} N+ N- MODEL_NAME [area]`
 *
 * Examples:
 *   D1 anode cathode DDEFAULT
 *   D2 a b 1N4148
 *   Dz net1 0 DZ_5V1     ← zener routes through this codec too
 *
 * Model dispatch:
 *   - Anything containing `LED` (any case) → schemaType "led"
 *     (handled by `ledCodec` first thanks to registry order).
 *   - Anything containing `ZENER`, starts with `DZ`, or ends in
 *     `_Z` → schemaType "zener".
 *   - Otherwise → schemaType "diode".
 *
 * The LED check on this codec is a guard — if `ledCodec` already
 * claimed the line we never get here, but defence in depth keeps
 * the diode codec correct in isolation.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
const KNOWN_ZENER_MODELS = new Set(["DZENER", "DZ", "1N4733", "1N4737"]);
export const diodeCodec = {
    spiceLetter: "D",
    schemaTypes: ["diode", "zener"],
    examples: ["D1 anode cathode DDEFAULT", "D2 a b 1N4148", "Dz net1 0 DZ_5V1"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("D needs 2 nodes and a model name");
        }
        const [na, nb, modelRaw] = card.tokens;
        if (na === undefined || nb === undefined || modelRaw === undefined) {
            return err("D has incomplete tokens");
        }
        const model = modelRaw.toUpperCase();
        if (model.includes("LED")) {
            return err("LED variant — defer to LED codec");
        }
        const isZener = model.includes("ZENER") ||
            model.startsWith("DZ") ||
            model.endsWith("_Z") ||
            KNOWN_ZENER_MODELS.has(model);
        const schemaType = isZener ? "zener" : "diode";
        const params = { model: modelRaw };
        const component = {
            id: card.identifier,
            type: schemaType,
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const fallbackModel = component.type === "zener" ? "DZENER" : "DDEFAULT";
        const model = stringParam(component.params.model, fallbackModel);
        const [a, b] = terminals;
        return `${component.id} ${a ?? "0"} ${b ?? "0"} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "diode" || schemaType === "zener";
    },
};
export { KNOWN_ZENER_MODELS };
//# sourceMappingURL=diode.js.map